/**
 * Auth Context Provider
 *
 * Provides authentication state and token throughout the React app.
 *
 * This is a lightweight auth context that wraps the existing localStorage-based auth.
 */

import React, { createContext, useContext, useState } from 'react';

const AuthContext = createContext(null);

// Parse a JSON value from localStorage, returning null on absence or corruption.
const readStoredJSON = (key) => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : null;
  } catch (err) {
    console.error(`Failed to parse stored ${key}:`, err);
    return null;
  }
};

export function AuthProvider({ children }) {
  // Hydrate synchronously from localStorage so token/user are populated on the
  // very first render. A null-first-render (the old useEffect-based load) made
  // downstream consumers — e.g. SiteContext — briefly see a logged-out state and
  // clear persisted selections (the "All Sites" reset-on-refresh bug).
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [user, setUser] = useState(() => readStoredJSON('user'));
  const [tenant, setTenant] = useState(() => readStoredJSON('tenant'));

  const login = (newToken, newUser, newTenant = null) => {
    setToken(newToken);
    setUser(newUser);
    setTenant(newTenant);
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(newUser));
    if (newTenant) {
      localStorage.setItem('tenant', JSON.stringify(newTenant));
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setTenant(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tenant');
  };

  const value = {
    token,
    user,
    tenant,
    login,
    logout,
    isAuthenticated: !!token
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    // Fallback for components not wrapped in AuthProvider
    return {
      token: localStorage.getItem('token'),
      user: (() => {
        try {
          const stored = localStorage.getItem('user');
          return stored ? JSON.parse(stored) : null;
        } catch {
          return null;
        }
      })(),
      tenant: (() => {
        try {
          const stored = localStorage.getItem('tenant');
          return stored ? JSON.parse(stored) : null;
        } catch {
          return null;
        }
      })(),
      isAuthenticated: !!localStorage.getItem('token'),
      login: () => {},
      logout: () => {}
    };
  }

  return context;
}
